// Shared formatting for the rate plan manager.
//
// Strings are translated lazily (i18n.t inside functions / getters), never at
// module load, so a language switch takes effect on the next render. Option
// lists carry `labelKey` for components that call t() themselves; `label` is a
// getter that returns the translated text for any other consumer.
import i18n from '../../i18n';

const option = (base, labelKey) => ({
  ...base,
  labelKey,
  get label() { return i18n.t(labelKey); },
});

export const WEEKDAYS = [
  option({ key: 'monday' }, 'rateFormat.weekdays.monday'),
  option({ key: 'tuesday' }, 'rateFormat.weekdays.tuesday'),
  option({ key: 'wednesday' }, 'rateFormat.weekdays.wednesday'),
  option({ key: 'thursday' }, 'rateFormat.weekdays.thursday'),
  option({ key: 'friday' }, 'rateFormat.weekdays.friday'),
  option({ key: 'saturday' }, 'rateFormat.weekdays.saturday'),
  option({ key: 'sunday' }, 'rateFormat.weekdays.sunday'),
];

export const RATE_TYPES = [
  option({ value: 'standard' }, 'rateFormat.rateTypes.standard'),
  option({ value: 'package' }, 'rateFormat.rateTypes.package'),
  option({ value: 'promotional' }, 'rateFormat.rateTypes.promotional'),
  option({ value: 'corporate' }, 'rateFormat.rateTypes.corporate'),
  option({ value: 'group' }, 'rateFormat.rateTypes.group'),
];

export const RATE_UNITS = [
  option({ value: 'night' }, 'rateFormat.rateUnits.night'),
  option({ value: 'week' }, 'rateFormat.rateUnits.week'),
  option({ value: 'month' }, 'rateFormat.rateUnits.month'),
];

export const UNIT_SUFFIX = {
  get night() { return i18n.t('rateFormat.unitSuffix.night'); },
  get week() { return i18n.t('rateFormat.unitSuffix.week'); },
  get month() { return i18n.t('rateFormat.unitSuffix.month'); },
};

// DRF errors arrive as {field: [msg]}, {non_field_errors: [...]},
// {detail: msg} or a list. Flatten to one readable line.
export const apiError = (err) => {
  const data = err?.response?.data;
  if (!data) return err?.message || i18n.t('rateFormat.errors.requestFailed');
  if (typeof data === 'string') return data;
  if (Array.isArray(data)) return data.join(' ');
  if (data.detail) return String(data.detail);
  return Object.entries(data)
    .map(([field, msgs]) => {
      const text = Array.isArray(msgs)
        ? msgs.map((m) => (typeof m === 'object' ? JSON.stringify(m) : m)).join(' ')
        : (typeof msgs === 'object' ? JSON.stringify(msgs) : msgs);
      return field === 'non_field_errors' ? text : `${field.replace(/_/g, ' ')}: ${text}`;
    })
    .join(' · ');
};

export const money = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : '—';
};

export const rulePeriodLabel = (rule, seasonsById) => {
  if (rule.season) {
    const s = seasonsById[rule.season];
    return s ? `${s.name} (${s.start_date} → ${s.end_date})` : (rule.season_name || i18n.t('rateFormat.period.season'));
  }
  if (rule.start_date && rule.end_date) return `${rule.start_date} → ${rule.end_date}`;
  return i18n.t('rateFormat.period.yearRound');
};

// "Sat +20%, Sun +10.00" — only the weekdays that actually adjust.
export const weekdaySummary = (rule) => WEEKDAYS
  .filter((d) => rule[`${d.key}_adjustment_type`] && rule[`${d.key}_adjustment_value`] != null
    && Number(rule[`${d.key}_adjustment_value`]) !== 0)
  .map((d) => {
    const value = Number(rule[`${d.key}_adjustment_value`]);
    const sign = value > 0 ? '+' : '';
    return rule[`${d.key}_adjustment_type`] === 'percentage'
      ? `${d.label} ${sign}${value}%`
      : `${d.label} ${sign}${value.toFixed(2)}`;
  })
  .join(', ');

export const tierRangeLabel = (t) => {
  if (t.max_nights == null || t.max_nights === '') {
    return i18n.t('rateFormat.tiers.orMore', { count: Number(t.min_nights) });
  }
  if (Number(t.max_nights) === Number(t.min_nights)) {
    return i18n.t('rateFormat.tiers.exact', { count: Number(t.min_nights) });
  }
  return i18n.t('rateFormat.tiers.range', { min: t.min_nights, max: t.max_nights });
};
